import PropTypes from "prop-types";
import React from "react";
import { Button, Plus } from "@opendate-app/setlist";
import { QUESTION_TYPES, SELECT_TYPE } from "../useCheckoutQuestions";

const CheckoutQuestionsEditor = ({ questions }) => {
  const {
    rows,
    addQuestion,
    updateQuestion,
    removeQuestion,
    addOption,
    updateOption,
    removeOption,
    duplicateNames,
  } = questions;

  return (
    <div className="mb-3" data-testid="checkout-questions">
      <small className="text-muted d-block mb-2" data-testid="checkout-questions-help">
        Asked once for each pass in an order, like the name to print on each pass. Answers show on
        the order and in the pass holder export.
      </small>

      {rows.map((row, index) => (
        <div className="pass-price-change" key={row.uid} data-testid={`checkout-question-${index}`}>
          <div className="pass-price-change__head">
            <span className="pass-price-change__title">{`Question ${index + 1}`}</span>
            {!row.answered && (
              <button
                type="button"
                className="pass-price-change__remove"
                aria-label={`Remove question ${index + 1}`}
                data-testid={`remove-checkout-question-${index}`}
                onClick={() => removeQuestion(row.uid)}
              >
                <i className="fa fa-times" />
              </button>
            )}
          </div>

          <div className="mb-2">
            <label className="form-label" htmlFor={`checkout-question-name-${index}`}>Question</label>
            <input
              id={`checkout-question-name-${index}`}
              type="text"
              maxLength={255}
              className="form-control"
              data-testid={`checkout-question-name-${index}`}
              placeholder="Name to print on the pass"
              value={row.name}
              onChange={(e) => updateQuestion(row.uid, { name: e.target.value })}
            />
          </div>

          <div className="mb-2">
            <label className="form-label" htmlFor={`checkout-question-type-${index}`}>Answer type</label>
            <select
              id={`checkout-question-type-${index}`}
              className="form-control"
              data-testid={`checkout-question-type-${index}`}
              aria-describedby={row.id ? `checkout-question-type-help-${index}` : undefined}
              disabled={!!row.id}
              value={row.type}
              onChange={(e) => updateQuestion(row.uid, { type: e.target.value })}
            >
              {QUESTION_TYPES.map((questionType) => (
                <option key={questionType.value} value={questionType.value}>{questionType.label}</option>
              ))}
            </select>
            {row.id && (
              <small className="text-muted" id={`checkout-question-type-help-${index}`}>
                The answer type can&apos;t change once a question is saved.
              </small>
            )}
          </div>

          {row.type === SELECT_TYPE && (
            <div className="mb-2" data-testid={`checkout-question-options-${index}`}>
              <label className="form-label">Choices</label>
              {row.options.map((option, optionIndex) => (
                <div className="input-group mb-1" key={option.uid}>
                  <input
                    type="text"
                    maxLength={255}
                    className="form-control"
                    aria-label={`Question ${index + 1} choice ${optionIndex + 1}`}
                    data-testid={`checkout-question-${index}-option-${optionIndex}`}
                    value={option.name}
                    onChange={(e) => updateOption(row.uid, option.uid, e.target.value)}
                  />
                  <button
                    type="button"
                    className="pass-price-change__remove"
                    aria-label={`Remove question ${index + 1} choice ${optionIndex + 1}`}
                    data-testid={`remove-checkout-question-${index}-option-${optionIndex}`}
                    onClick={() => removeOption(row.uid, option.uid)}
                  >
                    <i className="fa fa-times" />
                  </button>
                </div>
              ))}
              <Button
                variant="tertiary"
                size="sm"
                icon={Plus}
                data-testid={`add-checkout-question-option-${index}`}
                onClick={() => addOption(row.uid)}
              >
                Add a choice
              </Button>
            </div>
          )}

          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id={`checkout-question-required-${index}`}
              data-testid={`checkout-question-required-${index}`}
              checked={row.required}
              onChange={() => updateQuestion(row.uid, { required: !row.required })}
            />
            <label className="form-check-label" htmlFor={`checkout-question-required-${index}`}>
              Required
            </label>
          </div>

          {row.id && (
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id={`checkout-question-enabled-${index}`}
                data-testid={`checkout-question-enabled-${index}`}
                checked={row.enabled}
                onChange={() => updateQuestion(row.uid, { enabled: !row.enabled })}
              />
              <label className="form-check-label" htmlFor={`checkout-question-enabled-${index}`}>
                Ask at checkout
              </label>
            </div>
          )}

          {row.answered && (
            <small className="text-muted" data-testid={`checkout-question-answered-${index}`}>
              Buyers have already answered this, so it can&apos;t be removed. Turn off
              &ldquo;Ask at checkout&rdquo; to stop asking it.
            </small>
          )}
        </div>
      ))}

      {duplicateNames && (
        <small className="text-danger d-block mb-2" data-testid="checkout-questions-duplicate">
          Each question needs a different name.
        </small>
      )}

      <Button variant="tertiary" size="sm" icon={Plus} data-testid="add-checkout-question" onClick={addQuestion}>
        Add a question
      </Button>
    </div>
  );
};

CheckoutQuestionsEditor.propTypes = {
  questions: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    addQuestion: PropTypes.func.isRequired,
    updateQuestion: PropTypes.func.isRequired,
    removeQuestion: PropTypes.func.isRequired,
    addOption: PropTypes.func.isRequired,
    updateOption: PropTypes.func.isRequired,
    removeOption: PropTypes.func.isRequired,
    duplicateNames: PropTypes.bool.isRequired,
  }).isRequired,
};

export default CheckoutQuestionsEditor;
